import React, { useEffect, useState, useMemo } from 'react';
import { collection, getDocs, query, orderBy, where, limit, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, listAll, getDownloadURL, getBlob, getMetadata } from 'firebase/storage';
import { db, storage, auth } from '../../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import AdminOrOwnerRoute from '../../AdminOrOwnerRoute';
import LoadingSpinner from '../common/LoadingSpinner';
import { rolesOf, isAdminOrOwner } from '../../roles';

const PaymentRecord = () => {
  const [user] = useAuthState(auth);
  const [driverReports, setDriverReports] = useState([]);
  const [firestoreReports, setFirestoreReports] = useState([]);
  const [storageFiles, setStorageFiles] = useState([]);
  const [paymentRecords, setPaymentRecords] = useState([]); // Add this for actual payment records
  const [packages, setPackages] = useState({}); // Add this for package data
  const [users, setUsers] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  // The whole role list — a profile can hold several, so a single role string
  // can't answer "is this person an owner?".
  const [userRoles, setUserRoles] = useState([]);
  
  // Simple search state like PackageDashboard - no debouncing needed
  const [search, setSearch] = useState('');

  // Driver scope, shared by all three tabs: every record on this page belongs to
  // one driver, so the filter is answered once rather than per tab.
  const [driverFilter, setDriverFilter] = useState('all'); // 'all' | uid

  // Pagination states for Firestore reports
  const [firestorePage, setFirestorePage] = useState(0);
  const [firestoreRowsPerPage, setFirestoreRowsPerPage] = useState(10);
  
  // Pagination states for Storage files
  const [storagePage, setStoragePage] = useState(0);
  const [storageRowsPerPage, setStorageRowsPerPage] = useState(10);

  // Pagination for payment records
  const [paymentPage, setPaymentPage] = useState(0);
  const [paymentRowsPerPage, setPaymentRowsPerPage] = useState(10);

  // Filter states for date pickers
  const [firestoreStartDate, setFirestoreStartDate] = useState(null);
  const [firestoreEndDate, setFirestoreEndDate] = useState(null);
  const [storageStartDate, setStorageStartDate] = useState(null);
  const [storageEndDate, setStorageEndDate] = useState(null);

  // Selected report packages
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [selectedReportPackages, setSelectedReportPackages] = useState([]);
  const [selectedFirestoreReportId, setSelectedFirestoreReportId] = useState(null);
  
  // Add client cache state
  const [clientCache, setClientCache] = useState({}); // { "john doe": ["reportId1", "reportId2"] }

  const fetchDriverReports = async () => {
    if (!user) return;

    try {
      const reportsQuery = query(
        collection(db, 'driverReports'),
        //where('Document ID', '!=', 'invoiceNumber'),
        orderBy('createdAt', 'desc'),
        limit(100)
      );

      const reportsSnapshot = await getDocs(reportsQuery);
      const reports = reportsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setDriverReports(reports);

      // Collect all package IDs from the fetched driver reports and preload package docs
      const allPackageIds = new Set();
      reports.forEach(r => {
        if (r.packageIds && Array.isArray(r.packageIds)) {
          r.packageIds.forEach(pid => {
            if (pid) allPackageIds.add(pid);
          });
        }
      });

      if (allPackageIds.size > 0) {
        const packagesMap = await fetchPackagesByIds(Array.from(allPackageIds));
        setPackages(prev => ({ ...prev, ...packagesMap }));
        
        // Build client cache after packages are loaded
        buildClientCache(reports, packagesMap);
      }
    } catch (error) {
      console.error('Error fetching driver reports:', error);
    }
  }; 

  // Build cache mapping client names to report IDs
  const buildClientCache = (reports, packagesMap) => {
    const cache = {};
    
    reports.forEach(report => {
      if (report.packageIds && Array.isArray(report.packageIds)) {
        report.packageIds.forEach(packageId => {
          const pkg = packagesMap[packageId];
          if (pkg && pkg.recipient) {
            const clientName = pkg.recipient.toLowerCase().trim();
            if (clientName) {
              if (!cache[clientName]) {
                cache[clientName] = [];
              }
              if (!cache[clientName].includes(report.id)) {
                cache[clientName].push(report.id);
              }
            }
          }
        });
      }
    });

    setClientCache(cache);
  };

  // Helper to fetch package docs for an array of ids (returns map id -> package)
  const fetchPackagesByIds = async (packageIdsArray = []) => {
    const packagesData = {};
    if (!Array.isArray(packageIdsArray) || packageIdsArray.length === 0) return packagesData;

    const batchPromises = packageIdsArray.map(async (packageId) => {
      try {
        const packageSnapshot = await getDoc(doc(db, 'packages', packageId));
        if (packageSnapshot.exists()) {
          packagesData[packageId] = { id: packageSnapshot.id, ...packageSnapshot.data() };
        }
      } catch (err) {
        console.error(`Error fetching package`, err);
      }
    });

    await Promise.all(batchPromises);
    return packagesData;
  };

  useEffect(() => {
    fetchDriverReports();
  }, [user]);

  // Fetch packages for a given report object (or by index using fetchPackagesInReportByIndex)
  const fetchPackagesInReport = async (report) => {
    if (!report || !report.packageIds || !Array.isArray(report.packageIds) || report.packageIds.length === 0) {
      setSelectedReportId(null);
      setSelectedReportPackages([]);
      return;
    }

    try {
      setSelectedReportId(report.id);
      // fetch each package doc by id
      const packagePromises = report.packageIds.map(id => getDoc(doc(db, 'packages', id)));
      const snaps = await Promise.all(packagePromises);
      const packagesList = snaps
        .filter(s => s.exists())
        .map(s => ({ id: s.id, ...s.data() }));

      setSelectedReportPackages(packagesList);
    } catch (err) {
      setSelectedReportPackages([]);
      setSelectedReportId(null);
    }
  };

  // Convenience helper: fetch by index in firestoreReports array
  const fetchPackagesInReportByIndex = (index) => {
    const report = firestoreReports[index];
    return fetchPackagesInReport(report);
  };

  const fetchPaymentRecords = async () => {
    if (!user) return;
    
    try {
      
      // First, get all package IDs from driver reports
      const allPackageIds = new Set();
      
      // Get package IDs from the packageIds field in driver reports
      firestoreReports.forEach(report => {
        if (report.packageIds && Array.isArray(report.packageIds)) {
          report.packageIds.forEach(packageId => {
            if (packageId) {
              allPackageIds.add(packageId);
            }
          });
        }
      });

      if (allPackageIds.size === 0) {
        setPaymentRecords([]);
        setPackages({});
        return;
      }

      // Convert to array for processing
      const packageIdsArray = Array.from(allPackageIds);
      const payments = [];
      const packagesData = {};
      const batchSize = 10; // Firestore 'in' query limit

      // Fetch payments in batches
      for (let i = 0; i < packageIdsArray.length; i += batchSize) {
        const batch = packageIdsArray.slice(i, i + batchSize);
        
        try {
          const paymentsQuery = query(
            collection(db, 'payments'),
            where('packageId', 'in', batch)
          );
          
          const paymentsSnapshot = await getDocs(paymentsQuery);
          
          paymentsSnapshot.forEach(docSnapshot => {
            const paymentData = { id: docSnapshot.id, ...docSnapshot.data() };
            payments.push(paymentData);
          });
        } catch (error) {
          console.error(`Error fetching payments for batch:`, error);
        }
      }

      // Fetch packages using individual doc() calls
      const packagePromises = packageIdsArray.map(async (packageId) => {
        try {
          const packageRef = doc(db, 'packages', packageId);
          const packageSnapshot = await getDoc(packageRef);
          
          if (packageSnapshot.exists()) {
            return { 
              id: packageId, 
              data: packageSnapshot.data() 
            };
          } else {
            return null;
          }
        } catch (error) {
          return null;
        }
      });

      const packageResults = await Promise.all(packagePromises);
      packageResults.forEach(result => {
        if (result) {
          packagesData[result.id] = { id: result.id, ...result.data };
        }
      });

      // Sort payments by date (newest first)
      payments.sort((a, b) => {
        const dateA = a.paymentDate?.toDate?.() || new Date(a.paymentDate?.seconds * 1000) || new Date(0);
        const dateB = b.paymentDate?.toDate?.() || new Date(b.paymentDate?.seconds * 1000) || new Date(0);
        return dateB - dateA;
      });
      
      setPaymentRecords(payments);
      setPackages(packagesData);
    } catch (error) {
      console.error('Error fetching payment records:', error);
      setPaymentRecords([]);
      setPackages({});
    }
  };


  useEffect(() => {
    fetchData();
  }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      
      // Fetch these first since they don't depend on each other
      await Promise.all([
        fetchUsers(),
        fetchFirestoreReports(),
      ]);
      
      // Then fetch payment records after firestore reports are loaded
      await fetchPaymentRecords();
      
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load payment records. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const usersSnapshot = await getDocs(collection(db, 'users'));
      const usersData = {};
      usersSnapshot.forEach(doc => {
        usersData[doc.id] = { id: doc.id, ...doc.data() };
      });
      setUsers(usersData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchFirestoreReports = async () => {
    if (!user) return;
    
    try {
      // User docs are keyed BY uid and carry `userId`, not `uid` — the old
      // where('uid','==',...) lookup matched nothing, so owners silently fell
      // through to the driver-scoped query below and saw only their own reports.
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const roles = rolesOf(userDoc.exists() ? userDoc.data() : null);
      const canSeeAllReports = isAdminOrOwner(roles);
      setUserRoles(roles);

      let reportsQuery;
      if (canSeeAllReports) {
        // Owners can see all reports, ordered by date
        reportsQuery = query(
          collection(db, 'driverReports'),
          orderBy('reportDate', 'desc'),
          limit(100) // Limit for performance
        );
      } else {
        // Regular users only see their own reports
        reportsQuery = query(
          collection(db, 'driverReports'),
          where('driverId', '==', user.uid),
          orderBy('reportDate', 'desc'),
          limit(50)
        );
      }
      
      const reportsSnapshot = await getDocs(reportsQuery);
      const reports = [];
      reportsSnapshot.forEach(doc => {
        const data = doc.data();
        reports.push({ 
          id: doc.id, 
          ...data,
          // Ensure dates are properly handled
          reportDate: data.reportDate,
          createdAt: data.createdAt
        });
      });
      setFirestoreReports(reports);
    } catch (error) {
      console.error('Error fetching Firestore reports:', error);
    }
  };


  // Add this function to filter payment records by search
  const getFilteredPaymentRecords = () => {
    if (!search.trim()) {
      return paymentRecords;
    }

    const query = search.toLowerCase();
    
    return paymentRecords.filter(payment => {
      const pkg = packages[payment.packageId];
      if (!pkg) return false;
      
      // Search in customer/recipient name, phone, destination, tracking number
      return (
        pkg.recipient?.toLowerCase().includes(query) ||
        pkg.phone?.toLowerCase().includes(query) ||
        pkg.destination?.toLowerCase().includes(query) ||
        pkg.trackingNumber?.toLowerCase().includes(query) ||
        payment.reference?.toLowerCase().includes(query) ||
        payment.receiptNumber?.toLowerCase().includes(query)
      );
    });
  };

  // Update the payment records tab to include verify button
  const PaymentRecordsTab = () => {
    // Use driverReports (fetched by fetchDriverReports) and pagination controls already in state
    const reports = driverReports || [];

    // FILTER first (so pagination applies to filtered set) - now using client cache
    const filteredReports = reports.filter(report => {
      if (driverFilter !== 'all' && report.driverId !== driverFilter) return false;
      if (!search.trim()) return true;
      const q = search.toLowerCase();

      // driver name or invoice match
      const driverName = getDriverName(report.driverId).toLowerCase();
      const invoice = (report.invoiceNumber || '').toLowerCase();
      
      if (driverName.includes(q) || invoice.includes(q)) {
        return true;
      }

      // tracking number match in packageIds
      const trackingMatch = (report.packageIds || []).some(id => (id || '').toLowerCase().includes(q));
      if (trackingMatch) {
        return true;
      }

      // Fast client search using cache
      const clientMatchFromCache = Object.keys(clientCache).some(clientName => {
        if (clientName.includes(q)) {
          return clientCache[clientName].includes(report.id);
        }
        return false;
      });
      
      if (clientMatchFromCache) {
        return true;
      }

      // Fallback: direct package check (for newly loaded packages not in cache)
      const directClientMatch = (report.packageIds || []).some(pid => {
        const pkg = packages[pid];
        if (!pkg) return false;
        return (pkg.recipient || '').toLowerCase().includes(q);
      });

      // fallback: check paymentRecords that belong to this report's packages for client fields
      const paymentMatch = paymentRecords.some(pr => 
        (report.packageIds || []).includes(pr.packageId) && (
          (pr.customerName || '').toLowerCase().includes(q) ||
          (pr.recipient || '').toLowerCase().includes(q) ||
          (pr.reference || '').toLowerCase().includes(q)
        )
      );

      return directClientMatch || paymentMatch;
    });

    const startIndex = paymentPage * paymentRowsPerPage;
    const endIndex = startIndex + paymentRowsPerPage;
    const paginatedReports = paymentRowsPerPage === -1 ? filteredReports : filteredReports.slice(startIndex, endIndex);

    const handleChangePaymentRowsPerPage = (event) => {
      const value = event.target.value === 'all' ? -1 : parseInt(event.target.value, 10);
      setPaymentRowsPerPage(value);
      setPaymentPage(0);
    };

    // Click to fetch & toggle packages for a report
    const togglePackagesForReport = async (report) => {
      if (!report) return;
      if (selectedReportId === report.id) {
        // collapse
        setSelectedReportId(null);
        setSelectedReportPackages([]);
        return;
      }
      await fetchPackagesInReport(report);
    };

    return (
      <div className="mt-4">

        {/* Mobile Cards */}
        <div className="md:hidden space-y-3">
          {paginatedReports.map((report) => (
            <div key={report.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              {/* Card Header */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3 border-b border-gray-200">
                <div className="flex justify-between items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2">
                      <svg className="h-4 w-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="truncate">{getDriverName(report.driverId)}</span>
                    </h3>
                    <p className="text-xs text-gray-600 mt-0.5">
                      {report.reportDate?.toDate?.()?.toLocaleDateString() || report.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </p>
                    <p className="text-xs font-mono text-gray-700 mt-1 font-medium">
                      Invoice: {report.invoiceNumber || 'N/A'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-1 flex-shrink-0">
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                      report.pdfUrl ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {report.pdfUrl ? 'PDF' : 'No PDF'}
                    </span>
                    <span className={`px-2.5 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                      report.verified === true ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {report.verified === true ? 'Verified' : 'Pending'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Card Body */}
              <div className="px-4 py-3 space-y-2.5">
                <div className="grid grid-cols-3 gap-3">
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Packages</p>
                    <p className="text-lg font-semibold text-gray-900">
                      {report.packageCount || (report.packageIds ? report.packageIds.length : 0)}
                    </p>
                  </div>
                  <div className="text-center border-l border-r border-gray-200">
                    <p className="text-xs text-gray-500 mb-1">Delivered</p>
                    <p className="text-lg font-semibold text-green-600">
                      {report.deliveredCount || 0}
                    </p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-500 mb-1">Cancelled</p>
                    <p className="text-lg font-semibold text-red-600">
                      {report.cancelledCount || 0}
                    </p>
                  </div>
                </div>

                <div className="pt-2 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Total Amount</span>
                    <span className="text-xl font-bold text-green-600">
                      ${(report.totalDeliveredPrice || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Card Actions */}
              <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex gap-2">
                <button
                  onClick={() => togglePackagesForReport(report)}
                  className="flex-1 bg-white border border-gray-300 text-gray-700 px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  {selectedReportId === report.id ? '▼ Hide Packages' : '▶ View Packages'}
                </button>
                {report.pdfUrl && (
                  <button
                    onClick={() => handleDownload(report.pdfUrl, report.fileName || `report-${report.id}.pdf`)}
                    className="flex-1 bg-blue-600 text-white px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors"
                  >
                    📄 Download
                  </button>
                )}
              </div>

              {/* Expandable Package Details */}
              {selectedReportId === report.id && selectedReportPackages.length > 0 && (
                <div className="px-4 py-3 bg-blue-50 border-t border-blue-100 overflow-hidden">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">Package Details:</h4>
                  <div className="space-y-3">
                    {selectedReportPackages.map((p) => (
                      <div key={p.id} className="bg-white rounded-lg p-3 border-l-4 border-blue-500 shadow-sm overflow-hidden">
                        <div className="space-y-1.5">
                          <div className="flex justify-between gap-2">
                            <span className="text-xs text-gray-600 flex-shrink-0">Tracking:</span>
                            <span className="text-xs font-mono font-semibold text-gray-900 text-right break-all">{p.trackingNumber || p.id}</span>
                          </div>
                          <div className="flex justify-between gap-2">
                            <span className="text-xs text-gray-600 flex-shrink-0">Recipient:</span>
                            <span className="text-xs font-medium text-gray-900 text-right break-words">{p.recipient || 'Unknown'}</span>
                          </div>
                          <div className="flex justify-between gap-2">
                            <span className="text-xs text-gray-600 flex-shrink-0">Destination:</span>
                            <span className="text-xs text-gray-700 text-right break-words">{p.destination || 'N/A'}</span>
                          </div>
                          {p.phone && (
                            <div className="flex justify-between gap-2">
                              <span className="text-xs text-gray-600 flex-shrink-0">Phone:</span>
                              <span className="text-xs text-gray-700 text-right">{p.phone}</span>
                            </div>
                          )}
                          <div className="flex justify-between gap-2 pt-1 border-t border-gray-200">
                            <span className="text-xs text-gray-600 flex-shrink-0">Amount:</span>
                            <span className="text-sm font-bold text-gray-900">${Number(p.total || 0).toFixed(2)}</span>
                          </div>
                          <button
                            onClick={async () => {
                              const payment = paymentRecords.find(pr => pr.packageId === p.id && pr.driverSummaryUrl);
                              if (payment) {
                                await handleDownload(payment.driverSummaryUrl, `driver-summary-${payment.driverSummaryInvoice || payment.driverSummaryId || 'report'}.pdf`);
                              } else if (report.pdfUrl) {
                                await handleDownload(report.pdfUrl, report.fileName || `report-${report.id}.pdf`);
                              } else {
                                window.alert('No summary available for this package or report.');
                              }
                            }}
                            className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-600 bg-blue-50 hover:bg-blue-100"
                          >
                            Download Summary
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop Table */}
        <div className="hidden md:block mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-300">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice #</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Packages</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Delivered</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Cancelled</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {paginatedReports.map((report) => (
                  <React.Fragment key={report.id}>
                    <tr className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <svg className="h-4 w-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-sm font-medium text-gray-900">
                            {getDriverName(report.driverId)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {report.reportDate?.toDate?.()?.toLocaleDateString() || report.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className="font-mono text-gray-900 font-medium">
                          {report.invoiceNumber || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {report.packageCount || (report.packageIds ? report.packageIds.length : 0)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {report.deliveredCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {report.cancelledCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                          ${((report.totalDeliveredPrice || 0)).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex flex-col items-center space-y-1">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${report.pdfUrl ? 'bg-green-100 text-green-800 border-green-200' : 'bg-gray-100 text-gray-800 border-gray-200'}`}>
                            {report.pdfUrl ? 'Available' : 'No PDF'}
                          </span>
                          {/* Verification Status */}
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                            report.verified === true 
                              ? 'bg-blue-100 text-blue-800 border-blue-200' 
                              : 'bg-yellow-100 text-yellow-800 border-yellow-200'
                          }`}>
                            {report.verified === true ? 'Verified' : 'Pending'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2 flex-wrap">
                          {report.pdfUrl ? (
                            <button
                              onClick={() => handleDownload(report.pdfUrl, report.fileName || `report-${report.id}.pdf`)}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                            >
                              Download
                            </button>
                          ) : (
                            <button disabled className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-400 bg-gray-100 cursor-not-allowed">
                              No File
                            </button>
                          )}

                          <button
                            onClick={() => togglePackagesForReport(report)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            {selectedReportId === report.id ? 'Hide Packages' : 'View Packages'}
                          </button>

                          {/* Verify Button - only show if not verified */}
                          {report.verified !== true && (
                            <button
                              onClick={() => verifyReport(report.id)}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                            >
                              <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                              Verify
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded row showing packages for selected report */}
                    {selectedReportId === report.id && (
                      <tr className="bg-gray-50">
                        <td colSpan={9} className="px-6 py-4">
                          {selectedReportPackages.length === 0 ? (
                            <div className="text-sm text-gray-500">No package documents found for this report.</div>
                          ) : (
                            <div className="space-y-2">
                              {selectedReportPackages.map(p => (
                                <div key={p.id} className="flex justify-between items-center border border-gray-100 rounded p-2">
                                  <div>
                                    <div className="text-sm font-medium text-gray-900">{p.trackingNumber || p.id} — {p.recipient || 'Unknown'}</div>
                                    <div className="text-xs text-gray-500">{p.destination || 'N/A'} • {p.phone || ''}</div>
                                  </div>
                                  <div className="flex items-center gap-3">
                                    <div className="text-sm text-gray-700">${Number(p.total || 0).toFixed(2)}</div>
                                    <button
                                      onClick={async () => {
                                        // Try to download driver summary if payment record has one for this package
                                        // find a payment record for this package that has driverSummaryUrl
                                        const payment = paymentRecords.find(pr => pr.packageId === p.id && pr.driverSummaryUrl);
                                        if (payment) {
                                          await handleDownload(payment.driverSummaryUrl, `driver-summary-${payment.driverSummaryInvoice || payment.driverSummaryId || 'report'}.pdf`);
                                        } else if (report.pdfUrl) {
                                          await handleDownload(report.pdfUrl, report.fileName || `report-${report.id}.pdf`);
                                        } else {
                                          window.alert('No summary available for this package or report.');
                                        }
                                      }}
                                      className="inline-flex items-center px-2 py-1 border border-transparent text-xs font-medium rounded text-blue-600 bg-blue-50 hover:bg-blue-100"
                                    >
                                      Download Summary
                                    </button>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 mt-4">
          <div className="flex flex-col">
            <label className="text-xs text-gray-700 mb-1">Rows per page</label>
            <select
              value={paymentRowsPerPage === -1 ? 'all' : paymentRowsPerPage}
              onChange={handleChangePaymentRowsPerPage}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-w-[120px]"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value="all">All</option>
            </select>
          </div>

          {paymentRowsPerPage !== -1 && (
            <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
              <button onClick={() => setPaymentPage(0)} disabled={paymentPage === 0} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed">
                <span className="sr-only">First</span>
                ‹‹
              </button>
                <button onClick={() => setPaymentPage(Math.max(0, paymentPage - 1))} disabled={paymentPage === 0} className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed">
                  ‹
                </button>
                <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                  Page {paymentPage + 1} of {Math.max(1, Math.ceil(filteredReports.length / (paymentRowsPerPage === -1 ? filteredReports.length : paymentRowsPerPage)))}
                </span>
                <button onClick={() => setPaymentPage(Math.min(Math.ceil(filteredReports.length / (paymentRowsPerPage === -1 ? filteredReports.length : paymentRowsPerPage)) - 1, paymentPage + 1))} disabled={paymentPage >= Math.ceil(filteredReports.length / (paymentRowsPerPage === -1 ? filteredReports.length : paymentRowsPerPage)) - 1} className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed">
                  ›
                </button>
                <button onClick={() => setPaymentPage(Math.max(0, Math.ceil(filteredReports.length / (paymentRowsPerPage === -1 ? filteredReports.length : paymentRowsPerPage)) - 1))} disabled={paymentPage >= Math.ceil(filteredReports.length / (paymentRowsPerPage === -1 ? filteredReports.length : paymentRowsPerPage)) - 1} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed">
                  ››
                </button>
              </nav>
          )}
        </div>
      </div>
    );
  };

  const FirestoreReportsTab = () => {
    // Filter reports by date range
    const filteredReports = firestoreReports.filter(report => {
      if (driverFilter !== 'all' && report.driverId !== driverFilter) return false;

      const reportDate = report.reportDate?.toDate?.() || report.createdAt?.toDate?.() || null;

      if (!reportDate) return true; // Include reports without dates
      
      const isAfterStart = !firestoreStartDate || reportDate >= firestoreStartDate;
      const isBeforeEnd = !firestoreEndDate || reportDate <= firestoreEndDate;
      
      return isAfterStart && isBeforeEnd;
    });

    const startIndex = firestorePage * firestoreRowsPerPage;
    const endIndex = startIndex + firestoreRowsPerPage;
    const paginatedReports = firestoreRowsPerPage === -1 ? filteredReports : filteredReports.slice(startIndex, endIndex);

    const handleChangeFirestoreRowsPerPage = (event) => {
      const value = event.target.value === 'all' ? -1 : parseInt(event.target.value, 10);
      setFirestoreRowsPerPage(value);
      setFirestorePage(0);
    };

    const clearDateFilters = () => {
      setFirestoreStartDate(null);
      setFirestoreEndDate(null);
      setFirestorePage(0);
    };

    return (
      <div className="mt-4 md:mt-6">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            </svg>
            <h2 className="text-base md:text-lg font-semibold text-gray-900">
              Database Reports ({filteredReports.length} of {firestoreReports.length})
            </h2>
            {/* Mirrors the report query's own condition, so the badge can't
                claim "All Drivers" while the list is driver-scoped. */}
            {isAdminOrOwner(userRoles) && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                All Drivers
              </span>
            )}
          </div>
          
          {/* Desktop Date Filters */}
          <div className="hidden md:flex gap-4 items-center">
            <input
              type="date"
              value={firestoreStartDate ? firestoreStartDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                setFirestoreStartDate(e.target.value ? new Date(e.target.value) : null);
                setFirestorePage(0);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Start Date"
            />
            <input
              type="date"
              value={firestoreEndDate ? firestoreEndDate.toISOString().split('T')[0] : ''}
              onChange={(e) => {
                setFirestoreEndDate(e.target.value ? new Date(e.target.value) : null);
                setFirestorePage(0);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="End Date"
            />
            <button 
              onClick={clearDateFilters}
              disabled={!firestoreStartDate && !firestoreEndDate}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Clear Dates
            </button>
          </div>

          {/* Mobile Date Filters */}
          <div className="md:hidden space-y-2">
            <div className="flex gap-2">
              <input
                type="date"
                value={firestoreStartDate ? firestoreStartDate.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  setFirestoreStartDate(e.target.value ? new Date(e.target.value) : null);
                  setFirestorePage(0);
                }}
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Start Date"
              />
              <input
                type="date"
                value={firestoreEndDate ? firestoreEndDate.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  setFirestoreEndDate(e.target.value ? new Date(e.target.value) : null);
                  setFirestorePage(0);
                }}
                className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="End Date"
              />
            </div>
            {(firestoreStartDate || firestoreEndDate) && (
              <button 
                onClick={clearDateFilters}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                Clear Date Filters
              </button>
            )}
          </div>
        </div>
        
        {firestoreReports.length === 0 ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">No driver reports found in the database.</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {paginatedReports.map((report, index) => (
                <div key={report.id || index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-gray-50 to-slate-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex justify-between items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-base flex items-center gap-2">
                          <svg className="h-4 w-4 text-blue-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="truncate">{getDriverName(report.driverId)}</span>
                        </h3>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {report.reportDate?.toDate?.()?.toLocaleDateString() || report.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                        </p>
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                          report.pdfUrl ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {report.pdfUrl ? 'PDF' : 'No PDF'}
                        </span>
                        <span className={`px-2.5 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${
                          report.verified === true ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {report.verified === true ? 'Verified' : 'Pending'}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="px-4 py-3 space-y-2.5">
                    <div className="grid grid-cols-3 gap-3">
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Packages</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {report.packageCount || (report.packageIds ? report.packageIds.length : 0)}
                        </p>
                      </div>
                      <div className="text-center border-l border-r border-gray-200">
                        <p className="text-xs text-gray-500 mb-1">Delivered</p>
                        <p className="text-lg font-semibold text-green-600">
                          {report.deliveredCount || 0}
                        </p>
                      </div>
                      <div className="text-center">
                        <p className="text-xs text-gray-500 mb-1">Cancelled</p>
                        <p className="text-lg font-semibold text-red-600">
                          {report.cancelledCount || 0}
                        </p>
                      </div>
                    </div>

                    <div className="pt-2 border-t border-gray-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Total Amount</span>
                        <span className="text-xl font-bold text-green-600">
                          ${(report.totalDeliveredPrice || 0).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Card Actions */}
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex gap-2">
                    {report.pdfUrl && (
                      <button
                        onClick={() => handleDownload(report.pdfUrl, report.fileName || `report-${report.id}.pdf`)}
                        className="flex-1 bg-blue-600 text-white px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors"
                      >
                        📄 Download PDF
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (selectedFirestoreReportId === report.id) {
                          setSelectedFirestoreReportId(null);
                        } else {
                          setSelectedFirestoreReportId(report.id);
                          fetchPackagesInReport(report);
                        }
                      }}
                      className={`flex-1 border px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                        report.pdfUrl 
                          ? 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50 active:bg-gray-100' 
                          : 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700 active:bg-blue-800'
                      }`}
                    >
                      {selectedFirestoreReportId === report.id ? '▼ Hide Packages' : '▶ View Packages'}
                    </button>
                  </div>

                  {/* Expandable Package Details */}
                  {selectedFirestoreReportId === report.id && selectedReportPackages.length > 0 && (
                    <div className="px-4 py-3 bg-blue-50 border-t border-blue-100 overflow-hidden">
                      <h4 className="font-semibold text-gray-900 mb-3 text-sm">Package Details:</h4>
                      <div className="space-y-3">
                        {selectedReportPackages.map((p) => (
                          <div key={p.id} className="bg-white rounded-lg p-3 border-l-4 border-blue-500 shadow-sm overflow-hidden">
                            <div className="space-y-1.5">
                              <div className="flex justify-between gap-2">
                                <span className="text-xs text-gray-600 flex-shrink-0">Tracking:</span>
                                <span className="text-xs font-mono font-semibold text-gray-900 text-right break-all">{p.trackingNumber || p.id}</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-xs text-gray-600 flex-shrink-0">Recipient:</span>
                                <span className="text-xs font-medium text-gray-900 text-right break-words">{p.recipient || 'Unknown'}</span>
                              </div>
                              <div className="flex justify-between gap-2">
                                <span className="text-xs text-gray-600 flex-shrink-0">Destination:</span>
                                <span className="text-xs text-gray-700 text-right break-words">{p.destination || 'N/A'}</span>
                              </div>
                              {p.phone && (
                                <div className="flex justify-between gap-2">
                                  <span className="text-xs text-gray-600 flex-shrink-0">Phone:</span>
                                  <span className="text-xs text-gray-700 text-right">{p.phone}</span>
                                </div>
                              )}
                              <div className="flex justify-between gap-2 pt-1 border-t border-gray-200">
                                <span className="text-xs text-gray-600 flex-shrink-0">Amount:</span>
                                <span className="text-sm font-bold text-gray-900">${Number(p.total || 0).toFixed(2)}</span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Packages</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Delivered</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Cancelled</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total Amount</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedReports.map((report) => (
                    <tr key={report.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <svg className="h-4 w-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-sm font-medium text-gray-900">
                            {getDriverName(report.driverId)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {report.reportDate?.toDate?.()?.toLocaleDateString() || 
                         report.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {report.packageCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {report.deliveredCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-900">
                        {report.cancelledCount || 0}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                          ${(report.totalDeliveredPrice || 0).toFixed(2)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                          report.pdfUrl 
                            ? 'bg-green-100 text-green-800 border-green-200' 
                            : 'bg-gray-100 text-gray-800 border-gray-200'
                        }`}>
                          {report.pdfUrl ? 'Available' : 'No PDF'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center gap-2">
                          {report.pdfUrl ? (
                            <button
                              onClick={() => handleDownload(report.pdfUrl, report.fileName || `report-${report.id}.pdf`)}
                              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                              <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Download
                            </button>
                          ) : (
                            <button
                              disabled
                              className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-400 bg-gray-100 cursor-not-allowed"
                            >
                              No File
                            </button>
                          )}

                          {/* View packages for this report */}
                          <button
                            onClick={() => fetchPackagesInReport(report)}
                            className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                          >
                            View Packages
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="flex justify-between items-center mt-4">
              <div className="flex flex-col">
                <label className="text-xs text-gray-700 mb-1">Rows per page</label>
                <select
                  value={firestoreRowsPerPage === -1 ? 'all' : firestoreRowsPerPage}
                  onChange={handleChangeFirestoreRowsPerPage}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-w-[120px]"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value="all">All</option>
                </select>
              </div>
              
              {firestoreRowsPerPage !== -1 && (
                <div className="flex justify-center mt-4">
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setFirestorePage(0)}
                      disabled={firestorePage === 0}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">First</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 9H17a1 1 0 110 2h-5.586l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setFirestorePage(firestorePage - 1)}
                      disabled={firestorePage === 0}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {firestorePage + 1} of {Math.ceil(filteredReports.length / firestoreRowsPerPage)}
                    </span>
                    <button
                      onClick={() => setFirestorePage(firestorePage + 1)}
                      disabled={firestorePage >= Math.ceil(filteredReports.length / firestoreRowsPerPage) - 1}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setFirestorePage(Math.ceil(filteredReports.length / firestoreRowsPerPage) - 1)}
                      disabled={firestorePage >= Math.ceil(filteredReports.length / firestoreRowsPerPage) - 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Last</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L8.586 11H3a1 1 0 110-2h5.586L4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  const StorageFilesTab = () => {
    // Filter files by date range
    const filteredFiles = storageFiles.filter(file => {
      if (driverFilter !== 'all' && file.driverId !== driverFilter) return false;

      const fileDate = file.createdAt;

      if (!fileDate) return true; // Include files without dates
      
      const isAfterStart = !storageStartDate || fileDate >= storageStartDate;
      const isBeforeEnd = !storageEndDate || fileDate <= storageEndDate;
      
      return isAfterStart && isBeforeEnd;
    });

    const startIndex = storagePage * storageRowsPerPage;
    const endIndex = startIndex + storageRowsPerPage;
    const paginatedFiles = storageRowsPerPage === -1 ? filteredFiles : filteredFiles.slice(startIndex, endIndex);

    const handleChangeStorageRowsPerPage = (event) => {
      const value = event.target.value === 'all' ? -1 : parseInt(event.target.value, 10);
      setStorageRowsPerPage(value);
      setStoragePage(0);
    };

    const clearStorageDateFilters = () => {
      setStorageStartDate(null);
      setStorageEndDate(null);
      setStoragePage(0);
    };

    return (
      <div className="mt-4 md:mt-6">
        <div className="flex flex-col md:flex-row justify-between md:items-center gap-3 mb-4">
          <div className="flex items-center gap-2">
            <svg className="h-5 w-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-base md:text-lg font-semibold text-gray-900">
              Storage Files ({filteredFiles.length} of {storageFiles.length})
            </h3>
            {/* Mirrors the report query's own condition, so the badge can't
                claim "All Drivers" while the list is driver-scoped. */}
            {isAdminOrOwner(userRoles) && (
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                All Drivers
              </span>
            )}
          </div>
          
          {/* Desktop Date Filters */}
          <div className="hidden md:flex gap-2 items-center">
            <div className="flex flex-col">
              <label className="text-xs text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={storageStartDate ? storageStartDate.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  const newDate = e.target.value ? new Date(e.target.value) : null;
                  setStorageStartDate(newDate);
                  setStoragePage(0);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-xs text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={storageEndDate ? storageEndDate.toISOString().split('T')[0] : ''}
                onChange={(e) => {
                  const newDate = e.target.value ? new Date(e.target.value) : null;
                  setStorageEndDate(newDate);
                  setStoragePage(0);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <button
              onClick={clearStorageDateFilters}
              disabled={!storageStartDate && !storageEndDate}
              className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed mt-5"
            >
              Clear Dates
            </button>
          </div>

          {/* Mobile Date Filters */}
          <div className="md:hidden space-y-2">
            <div className="flex gap-2">
              <div className="flex-1 flex flex-col">
                <label className="text-xs text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={storageStartDate ? storageStartDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const newDate = e.target.value ? new Date(e.target.value) : null;
                    setStorageStartDate(newDate);
                    setStoragePage(0);
                  }}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div className="flex-1 flex flex-col">
                <label className="text-xs text-gray-700 mb-1">End Date</label>
                <input
                  type="date"
                  value={storageEndDate ? storageEndDate.toISOString().split('T')[0] : ''}
                  onChange={(e) => {
                    const newDate = e.target.value ? new Date(e.target.value) : null;
                    setStorageEndDate(newDate);
                    setStoragePage(0);
                  }}
                  className="px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            {(storageStartDate || storageEndDate) && (
              <button
                onClick={clearStorageDateFilters}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-gray-700 bg-white hover:bg-gray-50 active:bg-gray-100 transition-colors"
              >
                Clear Date Filters
              </button>
            )}
          </div>
        </div>
        
        {storageFiles.length === 0 ? (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">No files found in Firebase Storage.</p>
              </div>
            </div>
          </div>
        ) : (
          <>
            {/* Mobile Cards */}
            <div className="md:hidden space-y-3">
              {paginatedFiles.map((file, index) => (
                <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-red-50 to-pink-50 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-start gap-2">
                      <svg className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 text-sm break-words">
                          {file.name}
                        </h3>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {file.createdAt ? file.createdAt.toLocaleDateString() : 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Card Body */}
                  <div className="px-4 py-3 space-y-2">
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-xs text-gray-600 flex-shrink-0">Driver:</span>
                      <span className="text-sm font-medium text-gray-900 text-right truncate">
                        {file.driverName || 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-xs text-gray-600 flex-shrink-0">File Size:</span>
                      <span className="text-sm text-gray-700">
                        {file.size ? (file.size / 1024).toFixed(2) + ' KB' : 'N/A'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center gap-2">
                      <span className="text-xs text-gray-600 flex-shrink-0">Type:</span>
                      <span className="px-2.5 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800 whitespace-nowrap">
                        PDF
                      </span>
                    </div>
                  </div>

                  {/* Card Action */}
                  <div className="px-4 py-3 bg-gray-50 border-t border-gray-200">
                    <button
                      onClick={() => handleDownload(file.url, file.name)}
                      className="w-full bg-red-600 text-white px-3 py-2.5 rounded-lg text-sm font-medium hover:bg-red-700 active:bg-red-800 transition-colors"
                    >
                      📄 Download File
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block mt-4 overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Driver</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created Date</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">File Size</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                    <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedFiles.map((file, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <svg className="h-4 w-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm text-gray-900 truncate max-w-xs" title={file.name}>
                            {file.name}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <svg className="h-4 w-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <span className="text-sm font-medium text-gray-900">
                            {getDriverName(file.driverId)}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <svg className="h-4 w-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4m-6 8h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm text-gray-500">
                            {file.createdAt.toLocaleDateString()}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800 border border-gray-200">
                          {formatFileSize(file.size)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                          {file.contentType?.split('/')[1]?.toUpperCase() || 'PDF'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <button
                          onClick={() => handleDownload(file.downloadURL, file.name, file.fileRef)}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <svg className="-ml-0.5 mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          Download
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="flex justify-between items-center mt-4">
              <div className="flex flex-col">
                <label className="text-xs text-gray-700 mb-1">Rows per page</label>
                <select
                  value={storageRowsPerPage === -1 ? 'all' : storageRowsPerPage}
                  onChange={handleChangeStorageRowsPerPage}
                  className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 min-w-[120px]"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value="all">All</option>
                </select>
              </div>
              
              {storageRowsPerPage !== -1 && (
                <div className="flex justify-center">
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setStoragePage(0)}
                      disabled={storagePage === 0}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">First</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M15.707 15.707a1 1 0 01-1.414 0l-5-5a1 1 0 010-1.414l5-5a1 1 0 111.414 1.414L11.414 9H17a1 1 0 110 2h-5.586l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setStoragePage(storagePage - 1)}
                      disabled={storagePage === 0}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {storagePage + 1} of {Math.ceil(filteredFiles.length / storageRowsPerPage)}
                    </span>
                    <button
                      onClick={() => setStoragePage(storagePage + 1)}
                      disabled={storagePage >= Math.ceil(filteredFiles.length / storageRowsPerPage) - 1}
                      className="relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    <button
                      onClick={() => setStoragePage(Math.ceil(filteredFiles.length / storageRowsPerPage) - 1)}
                      disabled={storagePage >= Math.ceil(filteredFiles.length / storageRowsPerPage) - 1}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Last</span>
                      <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0l5 5a1 1 0 010 1.414l-5 5a1 1 0 01-1.414-1.414L8.586 11H3a1 1 0 110-2h5.586L4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  // Helper functions that are missing
  const getDriverName = (driverId) => {
    const driver = users[driverId];
    if (!driver) return 'Unknown Driver';
    // User docs are written with `name` (see AdminUserManagement); firstName and
    // lastName are kept only as a fallback for older records, which is why this
    // used to show bare email addresses for everyone.
    const full = driver.name
      || `${driver.firstName || ''} ${driver.lastName || ''}`.trim()
      || driver.displayName;
    return full || driver.email || 'Unknown Driver';
  };

  // Drivers offered in the filter: everyone the users table marks as a driver,
  // plus anyone who actually owns records here. The union matters because a
  // person whose role was changed later still has reports that must stay
  // reachable through the filter.
  const driverOptions = useMemo(() => {
    const ids = new Set();
    Object.entries(users).forEach(([uid, u]) => {
      if (Array.isArray(u.roles) && u.roles.includes('driver')) ids.add(uid);
    });
    driverReports.forEach(r => r.driverId && ids.add(r.driverId));
    firestoreReports.forEach(r => r.driverId && ids.add(r.driverId));
    storageFiles.forEach(f => f.driverId && ids.add(f.driverId));

    return [...ids]
      .map(uid => ({ uid, name: getDriverName(uid) }))
      .sort((a, b) => a.name.localeCompare(b.name));
    // getDriverName is a stable read over `users`, which is already a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, driverReports, firestoreReports, storageFiles]);

  // Changing who we are looking at invalidates every tab's page position.
  useEffect(() => {
    setPaymentPage(0);
    setFirestorePage(0);
    setStoragePage(0);
  }, [driverFilter]);

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownload = async (downloadURL, fileName, fileRef = null) => {
    try {
      if (fileRef) {
        // For storage files, get blob and create download link
        const blob = await getBlob(fileRef);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      } else {
        // For direct URLs, use fetch and blob approach
        const response = await fetch(downloadURL);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      // Fallback: open in new tab
      window.open(downloadURL, '_blank');
    }
  };

  // Add verify report function
  const verifyReport = async (reportId) => {
    if (!reportId) return;

    // Show confirmation dialog
    const confirmVerify = window.confirm(
      'Are you sure you want to mark this report as verified? This action will update the report status to verified.'
    );

    if (!confirmVerify) return;

    try {
      const reportRef = doc(db, 'driverReports', reportId);
      await updateDoc(reportRef, {
        verified: true,
        verifiedAt: new Date(),
        verifiedBy: user.uid
      });

      // Update local state to reflect the change
      setDriverReports(prev => 
        prev.map(report => 
          report.id === reportId 
            ? { ...report, verified: true, verifiedAt: new Date(), verifiedBy: user.uid }
            : report
        )
      );

    } catch (error) {
      console.error('Error verifying report:', error);
      alert('Failed to verify report. Please try again.');
    }
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Authenticating...</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading payment records...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
              <div className="mt-4">
                <button
                  onClick={fetchData}
                  className="bg-red-100 px-2 py-1 rounded text-sm text-red-800 hover:bg-red-200"
                >
                  Retry
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <AdminOrOwnerRoute>
      <div className="p-3 md:p-6">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Payment Records
        </h1>
        
        <p className="text-sm md:text-base text-gray-600 mb-4 md:mb-6">
          View and search payment records by customer, and download driver summary reports.
        </p>

        {/* Search and driver scope. The driver filter applies to all three tabs,
            since every record here belongs to exactly one driver. */}
        <div className="bg-white shadow-sm rounded-lg mb-4 md:mb-6">
          <div className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Search by driver, recipient, invoice, tracking number..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 px-3 md:px-4 py-2.5 md:py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <select
                value={driverFilter}
                onChange={(e) => setDriverFilter(e.target.value)}
                aria-label="Filter by driver"
                className="px-3 md:px-4 py-2.5 md:py-2 border border-gray-300 rounded-lg text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:min-w-[220px]"
              >
                <option value="all">All drivers</option>
                {driverOptions.map(d => (
                  <option key={d.uid} value={d.uid}>{d.name}</option>
                ))}
              </select>
              {(search || driverFilter !== 'all') && (
                <button
                  onClick={() => { setSearch(''); setDriverFilter('all'); }}
                  className="sm:px-4 py-2 bg-gray-500 text-white rounded-lg text-sm font-medium hover:bg-gray-600 active:bg-gray-700 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
            {driverFilter !== 'all' && (
              <p className="mt-2 text-xs text-gray-600">
                Showing records for <span className="font-semibold">{getDriverName(driverFilter)}</span> across all tabs.
              </p>
            )}
          </div>
        </div>

        {/* Mobile Tab Navigation */}
        <div className="md:hidden mb-4">
          <label htmlFor="tabs" className="sr-only">Select a tab</label>
          <select
            id="tabs"
            value={activeTab}
            onChange={(e) => setActiveTab(Number(e.target.value))}
            className="block w-full rounded-lg border-gray-300 py-2.5 pl-3 pr-10 text-base focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm shadow-sm"
          >
            <option value={0}>💳 Payment Records ({driverReports.length})</option>
            <option value={1}>📁 Database Reports ({firestoreReports.length})</option>
            <option value={2}>📄 Storage Files ({storageFiles.length})</option>
          </select>
        </div>

        {/* Desktop Tab Navigation */}
        <div className="hidden md:block border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab(0)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 0
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center">
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
                Payment Records ({driverReports.length})
              </span>
            </button>
            <button
              onClick={() => setActiveTab(1)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 1
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center">
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                </svg>
                Database Reports ({firestoreReports.length})
              </span>
            </button>
            <button
              onClick={() => setActiveTab(2)}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 2
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <span className="flex items-center">
                <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Storage Files ({storageFiles.length})
              </span>
            </button>
          </nav>
        </div>

        {activeTab === 0 && <PaymentRecordsTab />}
        {activeTab === 1 && <FirestoreReportsTab />}
        {activeTab === 2 && <StorageFilesTab />}
        
        <div className="mt-8">
          <hr className="border-gray-200" />
          <p className="mt-4 text-sm text-gray-500">
            Total Payment Records: {driverReports.length} | Total Reports: {firestoreReports.length} | Total Files: {storageFiles.length}
          </p>
        </div>
      </div>
    </AdminOrOwnerRoute>
  );
};

export default PaymentRecord;